import type { OnboardingProfile } from '../types';

export type OnboardingCardKey = 'brand' | 'business' | 'goal' | 'voice' | 'cadence';
export type OnboardingField = 'brandName' | 'businessType' | 'goal' | 'voice' | 'cadence';
export type OnboardingFocus = 'overview' | OnboardingField | 'review';

export interface OnboardingMessage {
  role: 'assistant' | 'user';
  text: string;
}

export interface OnboardingTurnRequest {
  message: string;
  profile?: Partial<OnboardingProfile>;
  history?: OnboardingMessage[];
}

export interface OnboardingTurnResponse {
  message: string;
  profile: Partial<OnboardingProfile>;
  unlocked: OnboardingCardKey[];
  suggestions: string[];
  readyToProceed: boolean;
  usedFallback: boolean;
}

export interface OnboardingQuestion {
  prompt: string;
  placeholder: string;
  suggestions: string[];
}

export const RECOMMENDATION_CHOICE = 'Let ThisBot recommend it';

export const EMPTY_ONBOARDING_PROFILE: OnboardingProfile = {
  brandName: '',
  businessType: '',
  timezone: 'America/Phoenix',
  goal: '',
  voice: '',
  cadence: '',
};

const CARD_FIELDS: Record<OnboardingCardKey, OnboardingField> = {
  brand: 'brandName',
  business: 'businessType',
  goal: 'goal',
  voice: 'voice',
  cadence: 'cadence',
};

const FIELD_CARDS: Record<OnboardingField, OnboardingCardKey> = {
  brandName: 'brand',
  businessType: 'business',
  goal: 'goal',
  voice: 'voice',
  cadence: 'cadence',
};

const REQUIRED_FIELDS: OnboardingField[] = ['brandName', 'businessType', 'goal'];

function cleanText(value: unknown, maxLength = 500) {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').slice(0, maxLength) : '';
}

export function normalizeProfile(profile?: Partial<OnboardingProfile>): OnboardingProfile {
  const detectedTimezone = typeof Intl === 'undefined'
    ? 'America/Phoenix'
    : Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Phoenix';

  return {
    brandName: cleanText(profile?.brandName, 80),
    businessType: cleanText(profile?.businessType, 120),
    timezone: cleanText(profile?.timezone, 80) || detectedTimezone,
    goal: cleanText(profile?.goal, 500),
    voice: cleanText(profile?.voice, 240),
    cadence: cleanText(profile?.cadence, 160),
    ...(cleanText(profile?.audience, 500) ? { audience: cleanText(profile?.audience, 500) } : {}),
  };
}

export function isOnboardingReady(profile: Partial<OnboardingProfile>) {
  const normalized = normalizeProfile(profile);
  return REQUIRED_FIELDS.every((field) => Boolean(normalized[field]));
}

export function getUnlockedCards(profile: Partial<OnboardingProfile>): OnboardingCardKey[] {
  const normalized = normalizeProfile(profile);
  return (Object.keys(CARD_FIELDS) as OnboardingCardKey[])
    .filter((card) => Boolean(normalized[CARD_FIELDS[card]]));
}

export function getOnboardingStep(profile: Partial<OnboardingProfile>) {
  const normalized = normalizeProfile(profile);
  if (!normalized.brandName || !normalized.businessType) return 0;
  if (!normalized.goal) return 1;
  if (!normalized.voice || !normalized.cadence) return 2;
  return 3;
}

export function getNextFocus(profile: Partial<OnboardingProfile>): OnboardingFocus {
  const normalized = normalizeProfile(profile);
  if (!normalized.brandName) return 'brandName';
  if (!normalized.businessType) return 'businessType';
  if (!normalized.goal) return 'goal';
  if (!normalized.voice) return 'voice';
  if (!normalized.cadence) return 'cadence';
  return 'review';
}

function recommendedVoice(profile: Partial<OnboardingProfile>) {
  const business = cleanText(profile.businessType).toLowerCase();
  if (/league|sport|gaming|esport|fitness/.test(business)) return 'Fast, energetic, and confident';
  if (/agency|consult|professional|real estate/.test(business)) return 'Expert, direct, and polished';
  if (/creator|personal/.test(business)) return 'Warm, human, and distinct';
  return 'Clean, confident, and human';
}

function recommendedCadence(profile: Partial<OnboardingProfile>) {
  const goal = cleanText(profile.goal).toLowerCase();
  if (/event|launch|registration|fill|enroll/.test(goal)) return '4 recorded posts a week, plus event reminders';
  if (/daily|consisten/.test(goal)) return 'Every weekday';
  return '3 recorded posts a week';
}

export function withRecommendedDefaults(profile: Partial<OnboardingProfile>): OnboardingProfile {
  const normalized = normalizeProfile(profile);
  if (!isOnboardingReady(normalized)) return normalized;
  return {
    ...normalized,
    voice: normalized.voice || recommendedVoice(normalized),
    cadence: normalized.cadence || recommendedCadence(normalized),
  };
}

export function getOnboardingQuestion(focus: OnboardingFocus, profile: Partial<OnboardingProfile>): OnboardingQuestion {
  const brand = cleanText(profile.brandName) || 'the account';
  switch (focus) {
    case 'brandName':
      return {
        prompt: 'What should I call the brand, league, business, or creator account?',
        placeholder: 'Example: Northstar League',
        suggestions: [],
      };
    case 'businessType':
      return {
        prompt: `What kind of business or community is ${brand}? Plain language is perfect.`,
        placeholder: 'Describe what you do',
        suggestions: ['Sports or gaming league', 'Local business', 'Creator or personal brand', 'Agency or multi-client team'],
      };
    case 'goal':
      return {
        prompt: `What should social media help ${brand} accomplish first?`,
        placeholder: 'Example: fill our fall league and make current players feel seen',
        suggestions: ['Grow the audience', 'Get more customers or members', 'Publish consistently', 'Promote an event or launch'],
      };
    case 'voice':
      return {
        prompt: `How should ${brand} sound? Choose a direction, write your own, or let me recommend the voice and rhythm.`,
        placeholder: 'Example: sharp, energetic, never corny',
        suggestions: ['Clean and confident', 'Fast and energetic', 'Warm and human', 'Expert and direct', RECOMMENDATION_CHOICE],
      };
    case 'cadence':
      return {
        prompt: 'What publishing rhythm feels realistic? Timing stays a draft until you approve it.',
        placeholder: 'Example: 4 recorded posts a week and event reminders',
        suggestions: ['3 times a week', 'Every weekday', 'Daily', RECOMMENDATION_CHOICE],
      };
    case 'review':
      return {
        prompt: `Your first brief for ${brand} is ready. Review any card before opening the private workspace.`,
        placeholder: '',
        suggestions: [],
      };
    case 'overview':
    default:
      return {
        prompt: 'In one sentence, tell me the account name, what it is, and what you want social media to do. I can pull several useful facts from one answer.',
        placeholder: 'Example: Northstar is an esports league that wants to fill fall registration',
        suggestions: ['Sports or gaming league', 'Local business', 'Creator or personal brand', 'Agency or multi-client team'],
      };
  }
}

function extractBrand(message: string) {
  const patterns = [
    /(?:brand|business|company|account|league|project)\s+(?:is\s+)?(?:called|named)\s+["']?([^,.;"']{2,80})/i,
    /(?:i run|i own|we run|we own|this is|for)\s+["']?([^,.;"']{2,80}?)(?=\s+(?:which|that|is|and wants|and needs)|[,.;]|$)/i,
    /^\s*["']?([^,.;"']{2,60}?)["']?\s+(?:is|are)\s+(?:an?|the)\s+(?=esports?|gaming|sports?|local|creator|personal|agency|restaurant|fitness|nonprofit|real estate|e-?commerce)/i,
  ];
  for (const pattern of patterns) {
    const match = message.match(pattern)?.[1];
    if (match) return cleanText(match.replace(/^(?:my|our)\s+/i, ''), 80);
  }
  return '';
}

function extractBusinessType(message: string) {
  const options: Array<[RegExp, string]> = [
    [/\b(?:esports?|gaming)\s+league\b/i, 'Sports or gaming league'],
    [/\bsports?\s+league\b|\bleague\b/i, 'Sports or gaming league'],
    [/\b(?:marketing|creative|social media)\s+agency\b|\bmulti-client\b/i, 'Agency or multi-client team'],
    [/\bcreator\b|\bpersonal brand\b/i, 'Creator or personal brand'],
    [/\blocal business\b/i, 'Local business'],
    [/\brestaurant\b/i, 'Restaurant or hospitality business'],
    [/\bfitness\b|\bgym\b/i, 'Fitness business or community'],
    [/\bnonprofit\b|\bnon-profit\b/i, 'Nonprofit organization'],
    [/\breal estate\b|\brealtor\b/i, 'Real estate business'],
    [/\be-?commerce\b|\bonline store\b/i, 'Ecommerce business'],
  ];
  return options.find(([pattern]) => pattern.test(message))?.[1] || '';
}

function extractGoal(message: string) {
  const intent = message.match(/(?:we|i)\s+(?:want|need|hope|plan|are trying)\s+(?:social(?: media)?\s+)?(?:to\s+)?([^.;]+?)(?=\s+(?:with|while|and (?:sound|post|publish)|using)\b|[.;]|$)/i)
    ?? message.match(/(?:goal|objective)\s+(?:is|:)?\s*(?:to\s+)?([^.;]+?)(?=\s+(?:with|while|and (?:sound|post|publish)|using)\b|[.;]|$)/i)
    ?? message.match(/(?:social media|social)\s+(?:should|needs? to|can)\s+([^.;]+?)(?=\s+(?:with|while|and (?:sound|post|publish)|using)\b|[.;]|$)/i);
  if (intent?.[1]) return cleanText(intent[1], 500);

  const goals: Array<[RegExp, string]> = [
    [/\bgrow\b.*\b(?:audience|followers?|community)\b/i, 'Grow the audience'],
    [/\b(?:customers?|members?|leads?|sales|registration|signups?|enrollment)\b/i, 'Get more customers or members'],
    [/\b(?:consistent|consistently|regularly)\b/i, 'Publish consistently'],
    [/\b(?:promote|launch|event)\b/i, 'Promote an event or launch'],
  ];
  return goals.find(([pattern]) => pattern.test(message))?.[1] || '';
}

function extractVoice(message: string) {
  const voiceClause = message.match(/(?:voice|tone|sound)\s*(?:is|:|like|should be)?\s*([^.;]+?)(?=\s+(?:and (?:post|publish)|posting|publishing)\b|[.;]|$)/i)?.[1];
  if (voiceClause && /[a-z]/i.test(voiceClause)) return cleanText(voiceClause.replace(/^[,;:\s]+/, ''), 240);

  const traits: Array<[RegExp, string]> = [
    [/\bfast\b.*\benergetic\b|\benergetic\b.*\bfast\b/i, 'Fast and energetic'],
    [/\bclean\b.*\bconfident\b|\bconfident\b.*\bclean\b/i, 'Clean and confident'],
    [/\bwarm\b.*\bhuman\b|\bhuman\b.*\bwarm\b/i, 'Warm and human'],
    [/\bexpert\b.*\bdirect\b|\bdirect\b.*\bexpert\b/i, 'Expert and direct'],
    [/\bplayful\b|\bwitty\b|\bbold\b|\bprofessional\b/i, cleanText(message.match(/\b(playful|witty|bold|professional)\b/i)?.[1], 240)],
  ];
  return traits.find(([pattern]) => pattern.test(message))?.[1] || '';
}

function extractCadence(message: string) {
  if (/\bevery\s+weekday\b|\bweekdays\b/i.test(message)) return 'Every weekday';
  if (/\bevery\s+day\b|\bdaily\b/i.test(message)) return 'Daily';
  const numbered = message.match(/\b(\d+)\s+(?:recorded\s+)?posts?\s+(?:a|per)\s+week\b/i)
    ?? message.match(/\b(\d+)\s+times?\s+(?:a|per)\s+week\b/i);
  if (numbered?.[1]) return `${numbered[1]} recorded posts a week`;
  const wordNumber = message.match(/\b(one|two|three|four|five|six|seven)\s+(?:recorded\s+)?(?:posts?|times?)\s+(?:a|per)\s+week\b/i)?.[1]?.toLowerCase();
  if (wordNumber) {
    const weeklyCount = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7 }[wordNumber as 'one' | 'two' | 'three' | 'four' | 'five' | 'six' | 'seven'];
    return `${weeklyCount} recorded post${weeklyCount === 1 ? '' : 's'} a week`;
  }
  if (/\btwice a week\b/i.test(message)) return '2 recorded posts a week';
  if (/\bonce a week\b/i.test(message)) return '1 recorded post a week';
  return '';
}

function assignFocusedAnswer(profile: OnboardingProfile, focus: OnboardingFocus, message: string) {
  if (focus === 'overview' || focus === 'review') return profile;
  if (message.toLowerCase() === RECOMMENDATION_CHOICE.toLowerCase()) {
    if (focus === 'voice') {
      return { ...profile, voice: recommendedVoice(profile), cadence: profile.cadence || recommendedCadence(profile) };
    }
    if (focus === 'cadence') return { ...profile, cadence: recommendedCadence(profile) };
  }
  return { ...profile, [focus]: cleanText(message, focus === 'brandName' ? 80 : 500) };
}

export function interpretDeterministicTurn(
  request: OnboardingTurnRequest,
  focus: OnboardingFocus = getNextFocus(request.profile || {}),
): OnboardingTurnResponse {
  const message = cleanText(request.message, 2_000);
  let profile = normalizeProfile(request.profile);
  if (!message) {
    const question = getOnboardingQuestion(focus, profile);
    return {
      message: question.prompt,
      profile,
      unlocked: getUnlockedCards(profile),
      suggestions: question.suggestions,
      readyToProceed: isOnboardingReady(profile),
      usedFallback: true,
    };
  }

  const extracted: Partial<OnboardingProfile> = {
    brandName: extractBrand(message),
    businessType: extractBusinessType(message),
    goal: extractGoal(message),
    voice: extractVoice(message),
    cadence: extractCadence(message),
  };
  profile = {
    ...profile,
    ...Object.fromEntries(Object.entries(extracted).filter(([, value]) => Boolean(value))),
  } as OnboardingProfile;

  const hasUsefulExtraction = Object.values(extracted).some(Boolean);
  if (!hasUsefulExtraction || (focus !== 'overview' && !extracted[focus as OnboardingField])) {
    profile = assignFocusedAnswer(profile, focus, message);
  }

  const readyToProceed = isOnboardingReady(profile);
  if (readyToProceed) profile = withRecommendedDefaults(profile);
  const nextFocus = getNextFocus(profile);
  const question = getOnboardingQuestion(nextFocus, profile);

  return {
    message: question.prompt,
    profile,
    unlocked: getUnlockedCards(profile),
    suggestions: question.suggestions,
    readyToProceed,
    usedFallback: true,
  };
}

export function cardForField(field: OnboardingField) {
  return FIELD_CARDS[field];
}
